import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { computed, Injectable, ResourceRef, signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { addDays, format } from 'date-fns';
import { catchError, finalize, Observable, of, ReplaySubject, tap, throwError } from 'rxjs';
import { AppInfo } from '../../../../global/environment';
import { OrbitUtils } from '../../../../global/orbit/orbit-utils';
import { ApiRouter } from '../../../../global/routes/api-routes';
import { Tracking } from '../../../../global/tracking/tracking';
import { DateHelpers } from '../../../../shared/utils/date-helpers';
import { CURRENT_FLOW_STATE, ORBIT_BUNDLE, ORBIT_BUNDLE_SESSION_KEY, PLAN_NUMBER } from '../../constants';
import { ServiceOptions } from '../../constants/service-options.enum';
import { FaultData, InspectBundle } from '../types/inspect-bundle';

@Injectable({
  providedIn: 'root',
})
export class OrbitFormClaimService {
  stillCantFindNumber = signal(false);
  genericModelNumberData = signal({ modelApplianceCombination: '', modelCode: '', genericModelNumber: '' });
  activeStep = signal(0);
  bundleKey = signal<string | undefined>(undefined);
  bundleFromParam = signal(false);
  currentStep = signal<string | undefined>(undefined);
  bundleValue = signal<InspectBundle | undefined>(undefined);
  latestFormSubmissionResponse = signal<any | undefined>(undefined);
  bookingData = signal<any | undefined>(undefined);
  faultStartDate = signal<string | undefined>(undefined);
  vcPayload = signal<any>(undefined);
  repairDataLoading = signal(false);
  vcCaptureLoading = signal(false);

  formError = signal<HttpErrorResponse | undefined>(undefined);
  vcFormError = signal<HttpErrorResponse | undefined>(undefined);
  pendingSessionClaimType = signal<string | undefined>(undefined);
  initialFormValueLoaded = new ReplaySubject<Record<string, any>>(1);

  modelNumberLookupError = signal(false);
  noAvailabilitySource = signal<string | undefined>(undefined);

  private getEndRoute(statusCode: string, data: any): string {
    const endRoutes: Record<string, () => string> = {
      PA: () => {
        if (data?.isTVRemoteReplacement) {
          this.storedValue['repair-details'] = {
            ...this.storedValue['repair-details'],
            isTVRemoteReplacement: true,
          };
        }
        return 'confirm-your-details';
      },
      GQ: () => 'repair-details',
      GS: () => {
        if (data.paymentRequired && data.selectedServiceOption) {
          // store this so it can go through the same flow
          this.storedValue['service-options'] = { option: data.selectedServiceOption };
          this.storeCurrentClaimStateForResuming('confirm-your-details');
          return 'payment';
        }

        if (data?.daysWithSlots?.length === 0) {
          this.noAvailabilitySource.set('GetServiceOption');
          return 'no-engineer';
        }

        return 'service-options';
      },
      PS: () => {
        const selectedServiceOption = this.storedValue['service-options']?.option ?? data.selectedServiceOption;
        const { serviceOptionType } = selectedServiceOption;
        const paymentRequired = selectedServiceOption.paymentRequired;

        // If we auto select a service then fill it in in case of page refresh
        if (!this.storedValue['service-options']?.option) {
          this.storedValue['service-options'] = {
            option: data.selectedServiceOption,
          };
          this.storeCurrentClaimStateForResuming(this.currentStep()!);
        }

        if (paymentRequired) {
          return 'payment';
        }
        console.log(selectedServiceOption);

        if (serviceOptionType === 'Home Repair') {
          const hasNoAvailability = data?.daysWithSlots?.length === 0;
          if (hasNoAvailability) {
            this.noAvailabilitySource.set('PutServiceOption');
            return 'no-engineer';
          }
          return 'book-engineer';
        }

        if (serviceOptionType === 'Courier') {
          return 'book-collection';
        }
        return '';
      },
      RD: () => {
        if (data) {
          this.bookingData.set(data);
        }
        return 'confirmation';
      },
    };
    return endRoutes[statusCode]?.() ?? '';
  }

  claimBundle: ResourceRef<InspectBundle> = rxResource({
    params: () => this.bundleKey(),
    stream: ({ params }) => {
      const bundle = params;
      if (bundle === undefined) return of({} as InspectBundle);

      if (!this.bundleFromParam()) {
        const data = JSON.parse(sessionStorage.getItem(ORBIT_BUNDLE) ?? '');
        const currentFormState = JSON.parse(sessionStorage.getItem(CURRENT_FLOW_STATE) ?? '{}');
        this.storedValue = currentFormState.storedValue ?? {};
        this.latestFormSubmissionResponse.set(currentFormState.latestFormSubmission);
        this.claimID = currentFormState.claimID ?? currentFormState.latestFormSubmission?.claimID;
        if (!this.currentStep()) {
          this.currentStep.set(currentFormState.currentStep);
        }
        this.handleLoadedInData(data);
        return of(data);
      }

      return this.http
        .post<InspectBundle>(ApiRouter.orbit.getBundle(), {
          bundle: bundle,
        })
        .pipe(
          tap((data: InspectBundle) => {
            sessionStorage.setItem(ORBIT_BUNDLE, JSON.stringify(data));
            const currentFormState = JSON.parse(sessionStorage.getItem(CURRENT_FLOW_STATE) ?? '{}');
            this.claimID = currentFormState.claimID ?? currentFormState.latestFormSubmission?.claimID;
            this.handleLoadedInData(data);
          })
        );
    },
  });

  formSubmissionData = signal<{ methodToCall: string; data: any } | undefined>(undefined);
  formSubmission = rxResource({
    params: () => this.formSubmissionData(),
    stream: ({ params }) => {
      if (!params) return of();

      return this.http
        .post(ApiRouter.orbit.formStepEndpoint(params.methodToCall), {
          ...params.data,
          'X-Session-Bundle': sessionStorage.getItem(ORBIT_BUNDLE_SESSION_KEY),
        })
        .pipe(
          tap((data: any) => {
            if (data.claimID) {
              this.claimID = data.claimID;
            }
            if (data.updatedBundle) {
              sessionStorage.setItem(ORBIT_BUNDLE_SESSION_KEY, JSON.stringify(data.updatedBundle));
            }

            const orbitStatusCode = data?.statusCode ?? data?.status;

            if (orbitStatusCode === 'RD013') {
              const thirdPartyError = new HttpErrorResponse({
                error: {
                  ...data,
                  status: 'RD013',
                },
              });
              this.setFormError(thirdPartyError);
              OrbitUtils.sendUserToErrorPage(thirdPartyError);
              return;
            }

            if (typeof orbitStatusCode !== 'string' || orbitStatusCode.length < 2) {
              console.warn('Unable to process orbit response without a valid status code', data);
              return;
            }

            const statusCode = orbitStatusCode.substring(0, 2) as string;

            if (statusCode === 'GM') {
              this.bundleValue.update((val) => {
                if (!val) return val;

                if (data.startTransaction) {
                  val.startTransaction = data.startTransaction;
                  delete data.startTransaction;
                }

                if (data.includeModelNumberPage) {
                  const dataTypes = (data.oEMApplianceData ?? []).map((item: any) => item.dataType);
                  const isElectrolux = data.bookingOEM?.toLowerCase() === 'electrolux';
                  const isPNCStep = isElectrolux && dataTypes.includes('PNCNumber');
                  const stepName = isPNCStep ? 'product-number' : 'model-number';

                  if (!val.stepsToGoThrough.includes(stepName)) {
                    const faultDetailsIndex = val.stepsToGoThrough.indexOf('fault-details') ?? 0;
                    val.stepsToGoThrough.splice(faultDetailsIndex, 0, stepName);
                  }
                }

                val.mandatoryData = data;
                val.isFreeTextSearch = data.bookingOEM === 'Whirlpool' && data.oEMProductGroupCode !== '';

                return val;
              });

              sessionStorage.setItem(ORBIT_BUNDLE, JSON.stringify(this.bundleValue()));

              this.router.navigate([
                this.bundleValue()?.bundle.planNumber,
                'orbit',
                this.bundleValue()?.stepsToGoThrough[this.activeStep()],
              ]);

              return;
            }

            if (statusCode === 'RD') {
              if (params.data.appointmentDate && this.state.latestStep?.earlierSlot) {
                Tracking.capture('date_booked_tracking', {
                  productType: this.state.claimData.mandatoryData.productType,
                  earliestDate: this.state.latestStep.earlierSlot,
                  selectedDate: params.data.appointmentDate,
                  manufacturer: this.state.claimData.bundle.manufacturer,
                  bff: params.data.bringForwardFlag,
                  bookingOEM: this.state.claimData.mandatoryData.bookingOEM,
                  latestPossibleDate: this.state.latestStep.availabilityEndDate,
                  numberOfDaysFromEarliestDate: DateHelpers.getDaysBetweenDates(
                    this.state.latestStep.earlierSlot,
                    params.data.appointmentDate
                  ),
                  numberOfDaysFromToday: DateHelpers.getDaysBetweenDates(
                    new Date().toISOString(),
                    params.data.appointmentDate
                  ),
                  numberOfDaysFromTodayToEarliestDate: DateHelpers.getDaysBetweenDates(
                    new Date().toISOString(),
                    this.state.latestStep.earlierSlot
                  ),
                });
              }
              // Booking complete in some form
              this.router.navigate([this.bundleValue()?.bundle.planNumber, 'orbit', 'confirmation'], {
                replaceUrl: true,
              });
            }

            this.latestFormSubmissionResponse.set(data);

            const currentStepPosition =
              this.bundleValue()?.stepsToGoThrough.findIndex((step: string) => step === this.currentStep()) ?? -1;

            const hasMoreSteps =
              currentStepPosition >= 0 && currentStepPosition < (this.bundleValue()?.stepsToGoThrough.length ?? 0) - 1;
            const nextStepFromArray = hasMoreSteps
              ? this.bundleValue()?.stepsToGoThrough[currentStepPosition + 1]
              : null;
            this.bundleValue.update((val) => {
              if (!val) return val;
              if (!hasMoreSteps) {
                val.stepsToGoThrough = [];
              }
              if (['GS', 'PS'].includes(statusCode)) {
                val.bundle.currentState = 'BOOK_ENGINEER';
              } else {
                val.bundle.currentState = 'INCOMPLETE_BOOKING';
              }
              return val;
            });

            sessionStorage.setItem(ORBIT_BUNDLE, JSON.stringify(this.bundleValue()));
            // check if there's more steps in the array when on the final step of stepsToGoThrough
            if (this.currentStep() === 'fault-details' && nextStepFromArray) {
              this.storeCurrentClaimStateForResuming(nextStepFromArray);
              this.router.navigate([this.state.claimData.bundle.planNumber, 'orbit', nextStepFromArray]);
              return;
            }
            const finalPageRoute = this.getEndRoute(statusCode, data);
            this.storeCurrentClaimStateForResuming(finalPageRoute);
            this.router.navigate([this.state.claimData.bundle.planNumber, 'orbit', finalPageRoute]);
          })
        );
    },
  });

  storedValue: Record<string, any> = {};

  get isGameReplacement(): boolean {
    return (
      this.currentStep() !== 'service-options' &&
      (this.storedValue?.['service-options']?.['option']?.serviceOptionLabel
        ?.toLowerCase()
        ?.includes('game exchange') ??
        false)
    );
  }

  claimID: string | undefined;

  private orbitClaimState = computed(() => {
    const mandatoryData = this.bundleValue()?.mandatoryData;
    const modelNumberOptional = !mandatoryData?.oEMModelDataRequired;
    const hasBVData = !!mandatoryData?.bevaluedCategoryID;
    return {
      stillCantFindNumber: this.stillCantFindNumber(),
      error: this.claimBundle.error(),
      isLoading: !this.bundleKey() || this.claimBundle.isLoading(),
      submittingValue: this.formSubmission.isLoading(),
      claimData: this.bundleValue()!,
      latestStep: this.latestFormSubmissionResponse(),
      formError: this.formError(),
      bookingData: this.bookingData(),
      genericModelNumberData: this.genericModelNumberData(),
      faultStartDate: this.faultStartDate(),
      modelNumberMandatory: !modelNumberOptional || (modelNumberOptional && hasBVData),
      bvModelNumberLookup: modelNumberOptional && hasBVData,
      modelNumberLookupError: this.modelNumberLookupError(),
    };
  });

  get state() {
    return this.orbitClaimState();
  }

  constructor(
    private readonly http: HttpClient,
    private readonly router: Router
  ) {}

  setFormError(error: HttpErrorResponse | undefined): void {
    this.formError.set(error);
  }

  setVCFormError(error: HttpErrorResponse | undefined): void {
    this.vcFormError.set(error);
  }

  scrollToMainContent() {
    document.body.scrollTop = document.documentElement.scrollTop = 0;
  }

  updateModelNumberState(state: boolean) {
    this.stillCantFindNumber.set(state);
  }

  updateGenericModelNumberData(data: {
    modelApplianceCombination: string;
    modelCode: string;
    genericModelNumber: string;
  }) {
    this.genericModelNumberData.set(data);
  }

  handleLoadedInData(data: InspectBundle, replaceUrl: boolean = false) {
    this.initialFormValueLoaded.next(this.storedValue);
    this.bundleKey.set(data.encryptedData);
    this.bundleValue.set(data);
    if (!this.storedValue['confirm-your-details']) {
      this.storedValue['confirm-your-details'] = {
        email: data.bundle.email,
        mobile: data.bundle.mobTel.replace('+44', '0'),
        landline: data.bundle.homeTel.replace('+44', '0'),
      };
    }

    if (data.getQuestion && !this.latestFormSubmissionResponse()) {
      this.latestFormSubmissionResponse.set(data.getQuestion);
    }

    if (this.currentStep() === 'confirmation') {
      this.bookingData.set(this.latestFormSubmissionResponse());
    }

    if (this.currentStep()) {
      this.router.navigate([data.bundle.planNumber, 'orbit', this.currentStep()], {
        replaceUrl,
      });
    } else {
      if (data.claimCreated) {
        if (data.bundle.currentState === 'BOOK_ENGINEER') {
          this.router.navigate([data.bundle.planNumber, 'orbit', 'confirm-your-details'], {
            replaceUrl,
          });
        } else {
          this.router.navigate([data.bundle.planNumber, 'orbit', 'repair-details'], {
            replaceUrl,
          });
        }
      } else {
        this.redirectClaimToGetMoreInformation(data);
      }
    }
  }

  getServiceOptionFieldMapping(requiredFields: string[] = []) {
    const { bundle } = this.state.claimData;
    const serviceOptionFieldMapping: Record<string, any> = {
      COLLECTION_ADDRESS: {
        buildingName: '',
        houseStreetName: bundle.addressLine1,
        localArea: bundle.addressLine2,
        townCity: bundle.addressLine3,
        postCode: bundle.postalCode,
      },
      DELIVERY_RETURN_ADDRESS: {
        buildingName: '',
        houseStreetName: bundle.addressLine1,
        localArea: bundle.addressLine2,
        townCity: bundle.addressLine3,
        postCode: bundle.postalCode,
      },
    };

    return requiredFields.reduce(
      (a, key: string) => {
        if (serviceOptionFieldMapping[key]) {
          a[key] = serviceOptionFieldMapping[key];
        }

        return a;
      },
      {} as Record<string, any>
    );
  }
  submitValueForStep(step: string): void {
    let objectToSend: Record<string, any> = {};
    let methodToCall: string | undefined;

    const generateServiceOptionRequest = () => {
      const values = this.storedValue['service-options'];
      methodToCall = 'PutServiceOption';
      const { startTransaction } = this.state.claimData;

      if (values.option['serviceOptionType'] === ServiceOptions.ManualReferral) {
        objectToSend = {
          ...objectToSend,
          appointmentDate: '',
          appointmentSlotIdentifier: '',
          claimID: this.claimID,
          excessPaid: false,
          extraInformation: '',
          GUID: startTransaction.gUID,
          planNo: this.state.claimData.bundle.planNumber,
          isManualReferral: true,
        };

        return;
      }

      objectToSend = {
        ...objectToSend,
        GUID: startTransaction.gUID,
        claimID: this.claimID,
        availabilityEndDate: format(addDays(new Date(), 6), 'yyyy-MM-dd'),
        availabilityStartDate: format(new Date(), 'yyyy-MM-dd'),
        serviceOptionID: values.option['serviceOptionID'],
        serviceOptionRequiredFields: this.getServiceOptionFieldMapping(values.option['serviceOptionRequiredFields']),
      };

      if (values.option.serviceOptionType === ServiceOptions.SelfSend) {
        delete objectToSend['availabilityEndDate'];
        delete objectToSend['availabilityStartDate'];

        objectToSend['metaData'] = {
          ...(objectToSend['metaData'] ?? {}),
          isSelfSend: true,
        };
      }

      if (values.option.serviceOptionType === 'Courier') {
        objectToSend['metaData'] = {
          ...(objectToSend['metaData'] ?? {}),
          isCourier: true,
        };
      }

      if (
        values.option.serviceOptionLabel.toLowerCase().includes('booking reference only') ||
        values.option.serviceOptionLabel.toLowerCase().includes('central repair (triage)')
      ) {
        delete objectToSend['availabilityEndDate'];
        delete objectToSend['availabilityStartDate'];

        objectToSend['metaData'] = {
          ...(objectToSend['metaData'] ?? {}),
          isSP: true,
        };
      }
    };

    const valueMapperFromStep: Record<string, () => void> = {
      'product-type': () => {
        methodToCall = 'StartTransaction';
        objectToSend = {
          manufacturer: this.state.claimData.bundle.manufacturer,
          planNumber: this.state.claimData.bundle.planNumber,
          ...this.storedValue['productType'],
        };
      },
      'fault-details': () => {
        let sectionValue = this.storedValue['fault-details'];
        methodToCall = 'UpdateTransaction';

        let faultCategoryID;
        let faultID;

        if (sectionValue['freeTextSearch']) {
          let faultData = this.state.claimData.mandatoryData.faultData;
          faultData = Array.isArray(faultData) ? faultData : [faultData];

          const faultCategory = faultData.find(
            (i) => i.faultCategoryName === sectionValue['freeTextSearch']['faultId_lv2']
          );

          const problemFaultId = faultCategory!.possibleAnswers.find(
            (answer) => `${answer.faultCode}` === `${sectionValue['freeTextSearch']['problemId_lv3']}`
          )?.faultID;

          faultCategoryID = faultCategory?.faultCategoryID;
          faultID = problemFaultId;
        } else {
          faultCategoryID = sectionValue['faultCategory']['faultCategoryID'];
          faultID = sectionValue['faultDetails']['faultID'];
        }
        objectToSend = {
          ...objectToSend,
          GUID: this.state.claimData.startTransaction.gUID,
          claimTypeID: this.state.claimData.bundle.claimTypeId,
          manufacturer: this.state.claimData.bundle.manufacturer,
          productType: this.state.claimData.mandatoryData.productType,
          faultCategoryID,
          faultID,
          applianceUseable: sectionValue['applianceUsable'],
        };
      },
      'product-number': () => {
        methodToCall = 'UpdateTransaction';
        const sectionValue = this.storedValue['product-number'];
        const oemData = this.state.claimData.mandatoryData.oEMApplianceData ?? [];
        const pncItem = oemData.find((item: any) => item.dataType === 'PNCNumber');
        const mlItem = oemData.find((item: any) => item.fieldIdentifier === 'MLCode');
        const pncValue = pncItem && sectionValue ? sectionValue[pncItem.fieldIdentifier] : undefined;
        const mlValue = mlItem && sectionValue ? sectionValue[mlItem.fieldIdentifier] : undefined;
        objectToSend = {
          ...objectToSend,
          GUID: this.state.claimData.startTransaction.gUID,
          channelCode: 'DGX',
          countryCode: 'GB',
          manufacturer: this.state.claimData.bundle.manufacturer,
          productType: this.state.claimData.mandatoryData.productType,
          claimTypeID: this.state.claimData.bundle.claimTypeId,
          modelNumber: pncValue?.dataLabel || pncValue,
          uniqueApplianceID: pncValue?.dataIdentifier,
          ...(mlValue !== undefined && { customOEMModelCode: mlValue }),
        };
      },
      'model-number': () => {
        methodToCall = 'UpdateTransaction';
        let sectionValue = this.storedValue['model-number'];
        if (sectionValue['cannot-find-number'] && !sectionValue['modelNumber']) {
          objectToSend = {
            ...objectToSend,
            GUID: this.state.claimData.startTransaction.gUID,
            manufacturer: this.state.claimData.bundle.manufacturer,
            modelNumber: this.state.bvModelNumberLookup
              ? (this.state.claimData.mandatoryData.modelNumber ?? '')
              : this.state.genericModelNumberData?.modelCode,
            uniqueApplianceID: this.state.bvModelNumberLookup
              ? undefined
              : this.state.genericModelNumberData?.genericModelNumber,
            claimTypeID: this.state.claimData.bundle.claimTypeId,
          };
        } else if (!sectionValue['modelNumber'] && !this.state.modelNumberMandatory) {
          objectToSend = {
            ...objectToSend,
            channelCode: 'DGX',
            countryCode: 'GB',
            GUID: this.state.claimData.startTransaction.gUID,
            manufacturer: this.state.claimData.bundle.manufacturer,
            productType: this.state.claimData.mandatoryData.productType,
            claimTypeID: this.state.claimData.bundle.claimTypeId,
          };
        } else {
          let modelNumber = sectionValue['modelNumber']['dataLabel'] || sectionValue['modelNumber'];

          if (this.state.bvModelNumberLookup && sectionValue['cannot-find-number']) {
            modelNumber = this.state.claimData.mandatoryData.modelNumber ?? '';
          }
          objectToSend = {
            ...objectToSend,
            GUID: this.state.claimData.startTransaction.gUID,
            manufacturer: this.state.claimData.bundle.manufacturer,
            modelNumber: modelNumber,
            productType: this.state.claimData.mandatoryData.productType,
            uniqueApplianceID: sectionValue['modelNumber']['dataIdentifier'],
            claimTypeID: this.state.claimData.bundle.claimTypeId,
          };
        }
      },
      'repair-details': () => {
        methodToCall = 'PutAnswer';
        const values = this.storedValue['repair-details'];

        let answerID = this.state.latestStep.answerData[0].answerID;
        const subAnswer = values[`${this.state.latestStep.questionID}_subAnswer`];

        let answerValue = values[answerID];

        if (this.state.latestStep.answerType === 'RESPONSE_FIELD') {
          answerValue = answerValue ?? '';
        }

        if (this.state.latestStep.answerType === 'DATE') {
          answerValue = new Date(answerValue).toString();
          this.faultStartDate.set(answerValue);
        }

        if (this.state.latestStep.answerType === 'RADIO_BUTTONS' || this.state.latestStep.answerType === 'LIST') {
          const { answerID: selectedID, answerLabel } = values[this.state.latestStep.questionID];

          answerID = selectedID;
          answerValue = answerLabel;
        }

        if (this.state.latestStep.answerType === 'RESPONSE_FIELD') {
          answerValue = values[this.state.latestStep.questionID];
        }

        if (subAnswer) {
          const { answerID: selectedID } = values[this.state.latestStep.questionID];

          answerID = selectedID;
          answerValue = subAnswer;
        }

        const effectiveAnswerType =
          subAnswer && values[this.state.latestStep.questionID]?.subQuestion
            ? 'RESPONSE_FIELD'
            : this.state.latestStep.answerType;

        objectToSend = {
          ...objectToSend,
          answerID,
          answerValue,
          answerType: effectiveAnswerType,
          claimID: this.claimID,
          GUID: this.state.claimData.startTransaction.gUID,
          questionID: this.state.latestStep.questionID,
        };
      },
      'confirm-your-details': () => {
        methodToCall = 'GetServiceOption';
        const { startTransaction, bundle } = this.state.claimData;
        const { addressLine1, addressLine2, addressLine3, postalCode, surname, customerTitle, firstName } = bundle;
        const email = this.storedValue['confirm-your-details'].email;
        const mobTel = this.storedValue['confirm-your-details'].mobile;
        const homeTel = this.storedValue['confirm-your-details'].landline;
        objectToSend = {
          ...objectToSend,
          customerTitle: customerTitle,
          customerFirstName: firstName,
          customerLastName: surname,
          customersEmail: email,
          customersHouseStreetName: addressLine1,
          customersLandLine: homeTel,
          customersLocalArea: addressLine2,
          customersMobile: mobTel,
          customersPostCode: postalCode,
          customersTownCity: addressLine3,
          GUID: startTransaction.gUID,
          claimID: this.claimID,
          ...(this.storedValue['repair-details']?.isTVRemoteReplacement && { isTVRemoteReplacement: true }),
        };
      },
      'service-options': () => {
        generateServiceOptionRequest();
      },
      payment: () => {
        generateServiceOptionRequest();
      },
      'smart-fix': () => {
        const { mandatoryData } = this.state.claimData;
        const movingOntoFaultDetails = this.bundleValue()?.stepsToGoThrough[this.activeStep()] === 'fault-details';
        if ((mandatoryData.faultData as FaultData[])?.length || !movingOntoFaultDetails) return;

        methodToCall = 'UpdateTransaction';

        objectToSend = {
          ...objectToSend,
          GUID: this.state.claimData.startTransaction.gUID,
          manufacturer: this.state.claimData.bundle.manufacturer,
          productType: this.state.claimData.mandatoryData.productType,
          claimTypeID: this.state.claimData.bundle.claimTypeId,
        };
      },
      'book-engineer': () => {
        const values = this.storedValue['book-engineer'];
        methodToCall = 'PutRepairData';
        const { startTransaction, bundle } = this.state.claimData;

        objectToSend = {
          ...objectToSend,
          GUID: startTransaction.gUID,
          claimID: this.claimID,
          collectionData: {},
          excessPaid: false,
          extraInformation: '',
          planNo: bundle.planNumber,
          appointmentDate: values.selectedSlot.date,
          appointmentSlotIdentifier: values.selectedSlot.id,
          bringForwardFlag: values.bringForwardFlag,
        };
      },
      'book-collection': () => {
        const values = this.storedValue['book-collection'];
        const serviceOption = this.storedValue['service-options'].option;
        methodToCall = 'PutRepairData';
        const { startTransaction, bundle } = this.state.claimData;

        objectToSend = {
          ...objectToSend,
          GUID: startTransaction.gUID,
          claimID: this.claimID,
          excessPaid: false,
          extraInformation: '',
          planNo: bundle.planNumber,
          appointmentDate: values['collection-date'].date,
          appointmentSlotIdentifier: values['collection-date'].id,
          collectionData: this.getServiceOptionFieldMapping(serviceOption['serviceOptionRequiredFields']),
        };
      },
    };

    if (!valueMapperFromStep[step]) {
      this.router.navigate(
        [this.bundleValue()?.bundle.planNumber, 'orbit', this.bundleValue()?.stepsToGoThrough[this.activeStep()]],
        {
          replaceUrl: true,
        }
      );
      console.log(`Stage: ${step} has no value mapping`);
      return;
    }
    valueMapperFromStep[step]();

    if (!methodToCall) {
      if (step === 'smart-fix') {
        this.router.navigate([
          this.bundleValue()?.bundle.planNumber,
          'orbit',
          this.bundleValue()?.stepsToGoThrough[this.activeStep()],
        ]);

        return;
      }

      throw new Error('No method set to call');
    }

    // Don't submit at this point as we need to capture a payment
    if (step === 'service-options' && this.state.latestStep.paymentRequired) {
      this.router.navigate([this.bundleValue()?.bundle.planNumber, 'orbit', 'payment'], {
        replaceUrl: true,
      });
      return;
    }

    this.formSubmissionData.set({
      methodToCall,
      data: objectToSend,
    });
  }

  storeValue(step: string, value: Record<string, any>): void {
    if (!this.storedValue[step]) {
      this.storedValue[step] = value;
      return;
    }

    this.storedValue[step] = {
      ...this.storedValue[step],
      ...value,
    };
  }

  moveBackStep(currentStep: string): void {
    const { bundle, stepsToGoThrough } = this.claimBundle.value() as any;
    const stepPosition = stepsToGoThrough.findIndex((i: string) => i === currentStep);
    if (stepPosition <= 0) {
      window.location.href = `${AppInfo.Environment.claimsURL}/my-plans/${bundle.planNumber}/claims`;
      return;
    }

    const previousStep = stepsToGoThrough[stepPosition - 1];
    this.storeCurrentClaimStateForResuming(previousStep);

    this.activeStep.set(stepPosition - 1);
    this.router.navigate([bundle.planNumber, 'orbit', previousStep]);
  }

  moveStepForward(currentStep: string): void {
    const { stepsToGoThrough } = this.state.claimData;
    const currentIndex = stepsToGoThrough.findIndex((i: string) => i === currentStep);
    const newStep = currentIndex + 1;
    this.activeStep.set(newStep);
    this.storeCurrentClaimStateForResuming(stepsToGoThrough[newStep]);
    this.submitValueForStep(currentStep);
  }

  storeCurrentClaimStateForResuming(step: string): void {
    sessionStorage.setItem(
      CURRENT_FLOW_STATE,
      JSON.stringify({
        currentStep: step,
        storedValue: this.storedValue,
        latestFormSubmission: this.latestFormSubmissionResponse(),
        claimID: this.claimID,
      })
    );
  }

  redirectClaimToGetMoreInformation(data: InspectBundle): void {
    if (data.stepsToGoThrough.length === 0) return;
    this.router.navigate([data.bundle.planNumber, 'orbit', data.stepsToGoThrough[0]], {
      replaceUrl: true,
    });
  }

  loadBundle(bundle: string, bundleFromQueryParams: boolean): void {
    this.bundleFromParam.set(bundleFromQueryParams);
    this.bundleKey.set(bundle);
  }

  deleteClaimSession(): void {
    [ORBIT_BUNDLE, CURRENT_FLOW_STATE, ORBIT_BUNDLE_SESSION_KEY].forEach((key: string) => {
      sessionStorage.removeItem(key);
    });
    this.router.navigate(['/', 'my-plans', this.state.claimData.bundle.planNumber]);
  }

  createBundle(createBundleObject: any): Observable<InspectBundle> {
    return this.http.post<InspectBundle>(ApiRouter.orbit.startClaim(), createBundleObject).pipe(
      tap((data: InspectBundle) => {
        sessionStorage.setItem(ORBIT_BUNDLE_SESSION_KEY, data.encryptedData);
        sessionStorage.setItem(ORBIT_BUNDLE, JSON.stringify(data));
        this.handleLoadedInData(data, true);
      }),
      catchError((error) => {
        this.setFormError(error);
        OrbitUtils.sendUserToErrorPage(error);
        return throwError(() => error);
      })
    );
  }

  sendClaimsVcCaptureForm() {
    return this.http.post(ApiRouter.orbit.vcCapture(), this.vcPayload()).subscribe();
  }

  sendCustomerSupportVcCaptureForm() {
    return this.http.post(ApiRouter.orbit.customerSupportVcCapture(), this.vcPayload()).subscribe();
  }

  submitVCCaptureForm(isCustomerSupport: boolean) {
    const url = isCustomerSupport ? ApiRouter.orbit.customerSupportVcCapture() : ApiRouter.orbit.vcCapture();
    this.vcCaptureLoading.set(true);
    this.http.post(url, this.vcPayload()).subscribe({
      next: () => {
        this.vcCaptureLoading.set(false);
      },
      error: (error) => {
        this.setVCFormError(error);
        this.vcCaptureLoading.set(false);
        this.scrollToMainContent();
      },
    });
  }

  removeCachedItems(): void {
    const keysToRemove = [ORBIT_BUNDLE, ORBIT_BUNDLE_SESSION_KEY, CURRENT_FLOW_STATE, PLAN_NUMBER];
    keysToRemove.forEach((key) => sessionStorage.removeItem(key));
  }

  getRepairData(claimID?: string) {
    const id = claimID ?? this.state.claimData.bundle.claimId;
    const decrypt = !!claimID;
    this.repairDataLoading.set(true);
    this.http
      .get<any>(ApiRouter.orbit.getRepairData(id, decrypt))
      .pipe(finalize(() => this.repairDataLoading.set(false)))
      .subscribe({
        next: (data) => {
          const faultSelected = this.formatFault(data.faultSelected);
          this.bookingData.set({ ...data, faultSelected });
          this.router.navigate([this.state.claimData.bundle.planNumber, 'orbit', 'confirmation'], {
            replaceUrl: true,
          });
          return data;
        },
        error: (error) => {
          this.setFormError(error);
          OrbitUtils.sendUserToErrorPage(error);
        },
      });
  }

  formatFault(faultString: string) {
    const numberString = faultString.match(/^\d+\s*-\s*(.*)$/);
    return numberString?.[1]?.trim() ?? faultString.trim();
  }
}
